import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreVertical,
  FileText,
  CheckCircle,
  Clock,
  MapPin,
  ArrowRight
} from 'lucide-react';
import { inspectionsApi, templatesApi } from '../services/api';
import { Inspection, Template } from '../types';
import toast from 'react-hot-toast';

const Inspections: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);

  const { data: inspections, isLoading } = useQuery('inspections', () =>
    inspectionsApi.getAll().then(r => r.data.inspections)
  );

  const { data: templates } = useQuery('templates', () =>
    templatesApi.getAll().then(r => r.data)
  );

  const deleteMutation = useMutation(
    (id: string) => inspectionsApi.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('inspections');
        toast.success('Inspection deleted');
      }
    }
  );

  const filteredInspections = inspections?.filter((i: Inspection) => {
    const matchesSearch = i.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         i.location?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !statusFilter || i.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-amber-500" />;
      default:
        return <FileText className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inspections</h1>
          <p className="text-gray-600">Manage and track your inspections</p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Inspection
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search inspections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      {/* Inspections List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : filteredInspections?.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No inspections yet</h3>
          <p className="text-gray-600 mt-2">Start your first inspection to get started</p>
          <button
            onClick={() => setShowNewModal(true)}
            className="mt-4 text-primary-600 hover:text-primary-700 font-medium"
          >
            Start inspection →
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="divide-y">
            {filteredInspections.map((inspection: Inspection) => (
              <div 
                key={inspection.id}
                onClick={() => navigate(`/inspections/${inspection.id}`)}
                className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="mt-1">{getStatusIcon(inspection.status)}</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{inspection.title}</h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {inspection.Template?.title || 'Unknown template'}
                        </span>
                        {inspection.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {inspection.location}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 mt-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(inspection.status)}`}>
                          {inspection.status.replace('_', ' ')}
                        </span>
                        {inspection.score !== null && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Score: {inspection.score}/{inspection.maxScore}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">
                      {inspection.completedAt 
                        ? new Date(inspection.completedAt).toLocaleDateString()
                        : inspection.startedAt 
                          ? new Date(inspection.startedAt).toLocaleDateString()
                          : 'Not started'
                      }
                    </span>
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New Inspection Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Start New Inspection</h2>
              <p className="text-gray-600 mb-4">Select a template to begin:</p>
              
              <div className="space-y-2">
                {templates?.map((template: Template) => (
                  <button
                    key={template.id}
                    onClick={() => {
                      navigate(`/inspections/new?template=${template.id}`);
                      setShowNewModal(false);
                    }}
                    className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
                  >
                    <div className="font-medium">{template.title}</div>
                    {template.description && (
                      <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                    )}
                    <div className="text-xs text-gray-500 mt-2">{template.questions?.length || 0} questions</div>
                  </button>
                ))}
              </div>
              
              <button
                onClick={() => setShowNewModal(false)}
                className="w-full mt-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inspections;